import heroBg from "@/assets/hero-botanical.webp";
import { useI18n } from "@/i18n/context";
import { AnimatePresence, motion } from "framer-motion";
import { useEffect, useState } from "react";

const WORD_CYCLE_MS = 2400;
const WORD_TRANSITION_S = 0.4;

const AnimatedWord = ({ words }: { words: readonly string[] }) => {
  const [index, setIndex] = useState(0);

  useEffect(() => {
    setIndex(0);
  }, [words]);

  useEffect(() => {
    const timer = window.setInterval(() => {
      setIndex((prev) => (prev + 1) % words.length);
    }, WORD_CYCLE_MS);
    return () => window.clearInterval(timer);
  }, [words]);

  const word = words[index % words.length];
  const longestWord = words.reduce((a, b) => (a.length > b.length ? a : b));

  return (
    <span
      className="inline-block relative align-baseline leading-[1.15]"
      aria-live="polite"
      aria-atomic="true"
    >
      {/* Sizer keeps width and height stable across word swaps */}
      <span className="invisible" aria-hidden="true">
        {longestWord}
      </span>
      <span className="absolute inset-0">
        <AnimatePresence mode="sync" initial={false}>
          <motion.span
            key={word}
            initial={{ opacity: 0, y: "0.2em" }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: "-0.2em" }}
            transition={{
              duration: WORD_TRANSITION_S,
              ease: [0.16, 1, 0.3, 1],
            }}
            className="absolute inset-0 whitespace-nowrap"
          >
            {word}
          </motion.span>
        </AnimatePresence>
      </span>
    </span>
  );
};

const HeroSection = () => {
  const { t } = useI18n();
  const animated = t.hero.animated;

  return (
    <section className="relative min-h-[100dvh] flex items-end overflow-hidden grain">
      <img
        src={heroBg}
        alt="Luxurious botanical interior with arched windows and tropical plants"
        className="absolute inset-0 w-full h-full object-cover"
        fetchPriority="high"
        loading="eager"
        decoding="sync"
        width={1920}
        height={1280}
      />

      {/* Multi-layer gradient for depth */}
      <div className="absolute inset-0 bg-gradient-to-t from-[#0e110d]/92 via-[#0e110d]/30 to-transparent" />
      <div className="absolute inset-0 bg-gradient-to-r from-[#0e110d]/40 via-transparent to-transparent" />

      {/* Top-right editorial marker */}
      <div
        className="absolute top-8 right-8 hidden lg:flex flex-col items-end gap-2 opacity-0 animate-fade-up"
        style={{ animationDelay: "600ms" }}
      >
        <span className="text-[9px] uppercase tracking-[0.4em] text-cream/40">French Riviera</span>
        <div className="w-8 h-px bg-cream/20" />
        <span className="text-[9px] uppercase tracking-[0.4em] text-cream/40">Est. 2019</span>
      </div>

      <div className="relative z-10 w-full max-w-[1400px] mx-auto px-6 md:px-8 lg:px-16 pb-12 md:pb-24 pt-24 md:pt-32">
        <div className="max-w-4xl">
          <div
            className="flex items-center gap-4 mb-8 opacity-0 animate-fade-up"
            style={{ animationDelay: "50ms" }}
          >
            <div className="w-10 h-px bg-[#b89a67]/60" />
            <span className="text-[10px] uppercase tracking-[0.4em] text-cream/50 font-sans">
              Visionature
            </span>
          </div>

          <h1
            className="font-serif font-light text-cream leading-[0.9] tracking-[-0.02em] opacity-0 animate-fade-up"
            style={{
              fontSize: "clamp(3rem, 7vw, 6rem)",
              lineHeight: "0.9",
              animationDelay: "120ms",
            }}
          >
            <span className="block">{animated.lead}</span>
            <span className="block flex flex-wrap xl:flex-nowrap items-baseline gap-x-[0.25em] gap-y-[0.04em]">
              {animated.before ? (
                <span className={animated.nowrap ? "whitespace-nowrap" : ""}>
                  {animated.before}
                  {animated.nowrap ? "\u00a0" : " "}
                  <AnimatedWord words={animated.words} />
                </span>
              ) : (
                <AnimatedWord words={animated.words} />
              )}
              {animated.after && (
                <span className="whitespace-nowrap">{animated.after}</span>
              )}
            </span>
          </h1>

          <div
            className="mt-8 md:mt-10 flex flex-wrap gap-4 opacity-0 animate-fade-up"
            style={{ animationDelay: "280ms" }}
          >
            <a
              href="#contact"
              className="inline-flex items-center gap-3 px-8 py-3.5 text-[11px] uppercase tracking-[0.25em] font-medium font-sans bg-[#b89a67] text-white hover:bg-[#a08558] transition-colors duration-300 active:scale-[0.97]"
            >
              {t.hero.cta1}
              <span className="inline-block transition-transform duration-300 group-hover:translate-x-1">→</span>
            </a>
            <a
              href="/catalogue"
              className="inline-flex items-center px-8 py-3.5 text-[11px] uppercase tracking-[0.25em] font-medium font-sans border border-cream/25 text-cream/90 hover:border-cream/50 hover:text-cream transition-all duration-300 active:scale-[0.97]"
            >
              {t.hero.cta2}
            </a>
          </div>

          <p
            className="mt-8 md:mt-10 text-[14px] text-cream/90 leading-relaxed font-sans max-w-md opacity-0 animate-fade-up"
            style={{ animationDelay: "400ms", textShadow: '0 1px 12px rgba(0,0,0,0.35)' }}
          >
            {t.hero.subtitle}
          </p>
        </div>

        {/* Scroll indicator — desktop only */}
        <div
          className="absolute bottom-8 left-1/2 -translate-x-1/2 hidden md:flex flex-col items-center gap-2 opacity-0 animate-fade-up"
          style={{ animationDelay: "900ms" }}
        >
          <div className="animate-scroll-bounce flex flex-col items-center gap-1.5">
            <div className="w-px h-8 bg-cream/25" />
            <div className="w-1 h-1 rounded-full bg-cream/40" />
          </div>
        </div>
      </div>
    </section>
  );
};

export default HeroSection;
